import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { parseTimeToMinutes } from './canonicalTimeMath';

/**
 * Timezone-aware time utilities for scheduling
 * 
 * CRITICAL: All scheduling must operate in the shop's local timezone to correctly
 * handle breaks, which are defined in shop-local HH:MM format.
 * 
 * These utilities ensure consistent conversion between:
 * - UTC ISO strings (database storage)
 * - Shop-local "minutes from midnight" (scheduling calculations)
 */

/**
 * Convert a UTC ISO string to minutes from midnight in the shop's timezone
 * 
 * @param utcISO - UTC ISO string from database (e.g., "2025-11-14T15:50:00.000Z")
 * @param timezone - Shop timezone (e.g., "America/New_York")
 * @returns Minutes from midnight in shop timezone (e.g., 650 for 10:50 AM)
 */
export function getMinutesInShopTZ(utcISO: string, timezone: string): number {
  const utcDate = new Date(utcISO);
  const zonedDate = toZonedTime(utcDate, timezone);
  const timeString = format(zonedDate, 'HH:mm');
  return parseTimeToMinutes(timeString);
}

/**
 * Create a UTC Date object from shop-local minutes and a date string
 * 
 * @param dateStr - Date in YYYY-MM-DD format
 * @param minutes - Minutes from midnight in shop timezone
 * @param timezone - Shop timezone (e.g., "America/New_York")
 * @returns UTC Date object
 */
export function makeUTCFromShopMinutes(
  dateStr: string,
  minutes: number,
  timezone: string
): Date {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const timeString = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  const shopLocalString = `${dateStr} ${timeString}`;
  return fromZonedTime(shopLocalString, timezone);
}

/**
 * Parse break times from shop-local HH:MM format to minutes from midnight
 * 
 * @param breaks - Array of break objects with start/end in HH:MM format
 * @returns Array of breaks with start/end as minutes from midnight
 */
export function parseBreaksToMinutes(breaks: Array<{ start: string; end: string }>): Array<{ start: number; end: number }> {
  return breaks
    .map(b => ({
      start: parseTimeToMinutes(b.start),
      end: parseTimeToMinutes(b.end)
    }))
    .filter(b => !isNaN(b.start) && !isNaN(b.end));
}

/**
 * Converts a datetime-local input value to UTC ISO string
 * @param datetimeLocal - Value from datetime-local input (YYYY-MM-DDTHH:mm or YYYY-MM-DD HH:mm)
 * @param timezone - Shop timezone (e.g., "America/Chicago")
 * @returns UTC ISO string suitable for database storage
 */
export function convertDatetimeLocalToUTC(
  datetimeLocal: string,
  timezone: string
): string {
  // Normalize format to include seconds
  const normalized = datetimeLocal.includes('T') 
    ? `${datetimeLocal}:00`
    : `${datetimeLocal.replace(' ', 'T')}:00`;
  
  // Convert from shop timezone to UTC
  const utcDate = fromZonedTime(normalized, timezone);
  return utcDate.toISOString();
}
